import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { OfferTermsLogo, ExpandableFeatureLayout } from '@lendica/components';
import { OfferTermsInvitation } from '@lendica/utils';
import { InvitedCustomersTable } from '../components/InvitedCustomersTable';
import { InviteDialog, InviteDialogStep } from '../components/InviteDialog';
import { ArchiveDialog } from '../components/ArchiveDialog';
import { CompanyInfoDialog } from '../components/CompanyInfoDialog';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOfferTermsApi } from '../api';
import { useOfferTermsActions } from '../hooks/useOfferTermsActions';
import { useCompanyContext, useActiveCompanyId, useContainerWidth, useUser } from '@lendica/hooks';

export const OfferTermsView = () => {
    const gridRef = useRef<AgGridReact>(null);
    const { isNarrow } = useContainerWidth();
    const { getInvitationsList } = useOfferTermsApi();
    const { getCompanyDetail, prefetchCompanyDetails, isCompanyResolved } = useCompanyContext();
    const user = useUser();
    const activeCompanyId = useActiveCompanyId();
    const company = activeCompanyId ? getCompanyDetail(activeCompanyId) : undefined;
    const [rows, setRows] = useState<OfferTermsInvitation[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [hasActiveFilters, setHasActiveFilters] = useState(false);

    const [selectedRow, setSelectedRow] = useState<OfferTermsInvitation | null>(null);
    const [openInviteDialog, setOpenInviteDialog] = useState(false);
    const [activeStep, setActiveStep] = useState<InviteDialogStep>(
        InviteDialogStep.CustomerInformation
    );

    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [openCompanyInfoDialog, setOpenCompanyInfoDialog] = useState(false);

    const loadPage = useCallback(
        async (nextPage: number, nextPageSize: number) => {
            const data = await getInvitationsList(nextPage, nextPageSize, false);
            setTotal(data.count || 0);
            const mapped: OfferTermsInvitation[] = data.results || [];
            setRows(mapped);
            setPage(nextPage);
            setPageSize(nextPageSize);
        },
        [getInvitationsList]
    );

    useEffect(() => {
        if (!isCompanyResolved) return;
        loadPage(1, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getInvitationsList, isCompanyResolved]);

    const pageCount = useMemo(
        () => (total && pageSize ? Math.ceil(total / pageSize) : 0),
        [total, pageSize]
    );

    const currentDataLength = rows.length;
    const startIndex = total ? (page - 1) * pageSize + (currentDataLength > 0 ? 1 : 0) : 0;
    const endIndex = total ? (page - 1) * pageSize + currentDataLength : 0;

    const handlePrevPage = useCallback(async () => {
        if (page <= 1) return;
        await loadPage(page - 1, pageSize);
    }, [page, pageSize, loadPage]);

    const handleNextPage = useCallback(async () => {
        if (pageCount && page >= pageCount) return;
        await loadPage(page + 1, pageSize);
    }, [page, pageSize, pageCount, loadPage]);

    const handlePageSizeChange = useCallback(
        async (event: any) => {
            const newSize = Number(event.target.value);
            if (!newSize || newSize === pageSize) return;
            await loadPage(1, newSize);
        },
        [pageSize, loadPage]
    );

    const handleSendInvite = useCallback((params: OfferTermsInvitation) => {
        setSelectedRow(params);
        setActiveStep(InviteDialogStep.ReviewInvitation);
        setTimeout(() => {
            setOpenInviteDialog(true);
        }, 100);
    }, []);

    const handleViewDetails = useCallback((params: OfferTermsInvitation) => {
        setSelectedRow(params);
        setActiveStep(InviteDialogStep.ReviewInvitation);
        setTimeout(() => {
            setOpenInviteDialog(true);
        }, 100);
    }, []);

    const handleArchive = useCallback(async (params: OfferTermsInvitation) => {
        setSelectedRow(params);
        setArchiveDialogOpen(true);
    }, []);

    const handleArchiveDialogClose = () => {
        setArchiveDialogOpen(false);
        setSelectedRow(null);
    };

    const handleInviteNewCustomer = () => {
        if (!company?.company_name || !company?.email || !user?.email) {
            setOpenCompanyInfoDialog(true);
            return;
        }
        setSelectedRow(null);
        setActiveStep(InviteDialogStep.CustomerInformation);
        setOpenInviteDialog(true);
    };

    const handleCompanyInfoDialogClose = () => {
        setOpenCompanyInfoDialog(false);
    };

    const handleCompanyInfoDialogSaved = useCallback(async () => {
        if (activeCompanyId) {
            await prefetchCompanyDetails([activeCompanyId], { force: true });
        }
        setOpenCompanyInfoDialog(false);
        setSelectedRow(null);
        setActiveStep(InviteDialogStep.CustomerInformation);
        setOpenInviteDialog(true);
    }, [activeCompanyId, prefetchCompanyDetails]);

    const handleInviteDialogClose = () => {
        setOpenInviteDialog(false);
        setSelectedRow(null);
        setActiveStep(InviteDialogStep.CustomerInformation);
    };

    const reloadCurrentPage = useCallback(async () => {
        await loadPage(page, pageSize);
    }, [loadPage, page, pageSize]);

    const checkForActiveFilters = useCallback(() => {
        if (gridRef.current?.api) {
            const filterModel = gridRef.current.api.getFilterModel();
            const hasFilters = Object.keys(filterModel || {}).length > 0;
            setHasActiveFilters(hasFilters);
        }
    }, []);

    const handleFilterChanged = useCallback(() => {
        checkForActiveFilters();
    }, [checkForActiveFilters]);

    const handleResetFilters = useCallback(() => {
        gridRef.current!.api.setFilterModel(null);
        setHasActiveFilters(false);
    }, []);

    return (
        <ExpandableFeatureLayout maxWidth={1280}>
            <Box
                sx={{
                    p: isNarrow ? 2 : 4,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 7,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <OfferTermsLogo height={23} />
                            <Chip label="NEW" size="small" variant="outlined" color="primary" />
                        </Box>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={handleInviteNewCustomer}
                        >
                            Invite customer
                        </Button>
                    </Box>
                    <Typography variant="subtitle2">
                        Invite your customers to use PayLater. Get paid today while your customers
                        pay over time.
                    </Typography>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: '5fr 3fr',
                            gap: 3,
                            mt: 2,
                        }}
                    >
                        <Alert
                            severity="info"
                            sx={{
                                gridColumn: {
                                    xs: 'span 2',
                                    md: 'span 1',
                                },
                            }}
                        >
                            <AlertTitle>How does OfferTerms work?</AlertTitle>
                            Your customer completes a one-time application. If approved, they can
                            delay payments up to 90 days based on credit. You're paid the full
                            invoice amount immediately while Lendica services and collects from your
                            customer. Your customer pays interest.
                        </Alert>
                        <Alert
                            severity="success"
                            sx={{
                                gridColumn: {
                                    xs: 'span 2',
                                    md: 'span 1',
                                },
                            }}
                        >
                            <AlertTitle>Prefer to cover the fees for your customers?</AlertTitle>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    alignItems: 'flex-start',
                                }}
                            >
                                Contact us to discuss your options.
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() =>
                                        window.open(
                                            'https://calendly.com/d/cv39-69m-rfb/daylit-introduction?utm_source=offerterms&utm_medium=portal&utm_campaign=offerterms',
                                            '_blank'
                                        )
                                    }
                                >
                                    Schedule a call
                                </Button>
                            </Box>
                        </Alert>
                    </Box>
                </Box>
                <Box
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        flex: 1,
                    }}
                >
                    <Box
                        sx={{
                            height: '100%',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                        >
                            <Typography variant="subtitle1" color="primary">
                                Invited customers
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 0,
                                    alignItems: 'center',
                                    px: 1,
                                }}
                            >
                                <Tooltip title="Reset all filters" placement="top">
                                    <IconButton
                                        size="small"
                                        color={'info'}
                                        onClick={handleResetFilters}
                                        sx={{
                                            'borderRadius': '4px',
                                            'backgroundColor': hasActiveFilters
                                                ? 'info.main'
                                                : 'transparent',
                                            'color': hasActiveFilters
                                                ? 'info.contrastText'
                                                : 'inherit',
                                            '&:hover': {
                                                backgroundColor: hasActiveFilters
                                                    ? 'info.dark'
                                                    : 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        <FilterListOffIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                        <InvitedCustomersTable
                            rowData={rows}
                            gridRef={gridRef}
                            useGridPagination={false}
                            paginationPageSize={pageSize}
                            onFilterChanged={handleFilterChanged}
                            getActionsForRow={useOfferTermsActions({
                                onSendInvite: handleSendInvite,
                                onResendInvite: handleSendInvite,
                                onViewDetails: handleViewDetails,
                                onArchive: handleArchive,
                            })}
                        />
                    </Box>
                    <Divider />
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1,
                        }}
                    >
                        <Typography variant="body2">Page Size:</Typography>
                        <Select
                            size="small"
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            color="info"
                            sx={{
                                'minWidth': 72,
                                '& .MuiSelect-select': {
                                    padding: '6px 12px',
                                },
                            }}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                        <Typography variant="body2" sx={{ mx: 1 }}>
                            {startIndex} to {endIndex} of {total || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ mx: 1 }}>
                            Page {pageCount ? page : 0} of {pageCount || 0}
                        </Typography>
                        <IconButton size="small" onClick={handlePrevPage} disabled={page <= 1}>
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleNextPage}
                            disabled={pageCount ? page >= pageCount : true}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Box>
            <InviteDialog
                open={openInviteDialog}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                selectedRow={selectedRow!}
                handleClose={handleInviteDialogClose}
                onSent={reloadCurrentPage}
            />
            <ArchiveDialog
                open={archiveDialogOpen}
                invitationId={selectedRow?.id || ''}
                handleClose={handleArchiveDialogClose}
                onArchived={reloadCurrentPage}
            />
            {company && (
                <CompanyInfoDialog
                    open={openCompanyInfoDialog}
                    onClose={handleCompanyInfoDialogClose}
                    onSaved={handleCompanyInfoDialogSaved}
                    company={company}
                />
            )}
        </ExpandableFeatureLayout>
    );
};
